import React from 'react';
import { FaCheck, FaEnvelope, FaExclamationTriangle, FaPaperPlane, FaTimes, FaUndo } from 'react-icons/fa';
import { FinField, FinTextarea } from './FinField';
import '../css/financeiro-parcela-pagar.css';

const ICONE = {
    enviar:    FaPaperPlane,
    ok:        FaCheck,
    danger:    FaTimes,
    alteracao: FaUndo,
    aviso:     FaExclamationTriangle,
};

/**
 * Confirmação de ação que passa a vez para OUTRO cargo — enviar, aprovar, reprovar, pedir
 * alteração. Um clique errado ali não se desfaz sozinho: avisa outra pessoa, muda o
 * pedido de etapa, apaga assinatura. Por isso nenhuma dessas ações roda direto do botão.
 *
 * Modal canônico do `.fin-pp` (skill accessmkt-ui). O tom escolhe a cor do Confirmar:
 * `ok` para aprovar, `danger` para reprovar, `primary` para o resto.
 *
 * @param {boolean}  aberto
 * @param {string}   titulo         pergunta curta ("Enviar ao Aéreo?")
 * @param {string}   subtitulo      consequência em uma linha
 * @param {string}   notifica       quem recebe e-mail, em frase inteira ("O Aéreo será
 *                                  notificado por e-mail."). Fica em destaque no corpo:
 *                                  quem confirma precisa saber que outra pessoa é avisada.
 * @param {string}   aviso          consequência que NÃO trava a ação mas pesa na decisão
 *                                  (ex.: opções que continuam no evento). Destaque amarelo.
 * @param {node}     children       conteúdo extra do corpo (aviso, checkbox)
 * @param {string}   rotulo         texto do botão de confirmar
 * @param {string}   rotuloFechar   texto do botão de desistir — troque quando a ação já se
 *                                  chama "Cancelar" (cancelar solicitação), senão os dois
 *                                  botões dizem a mesma palavra com sentidos opostos
 * @param {string}   tom            'primary' | 'ok' | 'danger'
 * @param {string}   icone          'enviar' | 'ok' | 'danger' | 'alteracao' | 'aviso'
 * @param {boolean}  processando    trava os botões enquanto a ação roda
 * @param {boolean}  confirmarPreso trava só o Confirmar (ex.: campo obrigatório no corpo)
 * @param {boolean}  largo          modal largo com o corpo rolando (conferência em lista no corpo)
 * @param {object}   alternativa    segunda saída que também confirma, por outro caminho:
 *                                  { rotulo, tom, preso, titulo, onConfirmar }. Fica entre o
 *                                  desistir e o confirmar.
 * @param {Function} onConfirmar    sem ele, o modal só mostra (uma revisão): fica só o botão de fechar
 * @param {Function} onFechar
 */
export default function ConfirmarAcaoModal({
    aberto, titulo, subtitulo, notifica = null, aviso = null, children, rotulo = 'Confirmar', rotuloFechar = 'Cancelar', tom = 'primary', icone = 'enviar',
    processando = false, confirmarPreso = false, largo = false, alternativa = null, onConfirmar, onFechar,
}) {
    if (!aberto) return null;

    const Icone = ICONE[icone] ?? FaCheck;
    const fechar = () => { if (!processando) onFechar(); };
    const classeIcone = tom === 'ok' ? ' is-ok' : tom === 'danger' ? ' is-danger' : '';

    return (
        <div className="modal fade show d-block fin-pp fin-pp__modalbg"
            onClick={(e) => { if (e.target === e.currentTarget) fechar(); }}>
            <div className={'modal-dialog modal-dialog-centered' + (largo ? ' modal-lg modal-dialog-scrollable' : '')}>
                <div className="modal-content fin-pp__modal">
                    <div className="fin-pp__modal-head">
                        <span className={'fin-pp__modal-head-ic' + classeIcone}><Icone size={16} /></span>
                        <h3 className="fin-pp__modal-title">{titulo}
                            {subtitulo && <span className="fin-pp__modal-sub">{subtitulo}</span>}
                        </h3>
                        <button type="button" className="fin-pp__modal-close" onClick={fechar} disabled={processando}>
                            <FaTimes size={16} />
                        </button>
                    </div>

                    {(notifica || aviso || children) && (
                        <div className="modal-body">
                            {notifica && (
                                <div className="fin-pp__notifica">
                                    <FaEnvelope size={13} style={{ flex: 'none', marginTop: 3 }} />
                                    <span>{notifica}</span>
                                </div>
                            )}
                            {aviso && (
                                <div className="fin-pp__notifica is-warn">
                                    <FaExclamationTriangle size={13} style={{ flex: 'none', marginTop: 3 }} />
                                    <span>{aviso}</span>
                                </div>
                            )}
                            {children}
                        </div>
                    )}

                    <div className="fin-pp__modal-foot">
                        <button type="button" className="fin-pp__btn" onClick={fechar} disabled={processando}>
                            {rotuloFechar}
                        </button>
                        {alternativa && (
                            <button type="button" className={'fin-pp__btn fin-pp__btn--' + (alternativa.tom ?? 'primary')}
                                title={alternativa.titulo || undefined}
                                onClick={alternativa.onConfirmar} disabled={processando || !!alternativa.preso}>
                                {processando ? 'Aguarde...' : alternativa.rotulo}
                            </button>
                        )}
                        {onConfirmar && (
                            <button type="button" className={'fin-pp__btn fin-pp__btn--' + tom}
                                onClick={onConfirmar} disabled={processando || confirmarPreso}>
                                {processando ? 'Aguarde...' : rotulo}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

/**
 * Aviso das opções de aéreo que já estão no evento (importadas). Não trava reprovar, pedir
 * alteração nem cancelar — a opção fica no evento e volta ligada se a malha for aprovada
 * de novo —, mas quem confirma precisa saber que o participante continua vendo.
 *
 * @param {number} n     quantas opções importadas
 * @param {string} deQue "desta malha" | "desta solicitação"
 * @param {string} acao  sujeito da frase ("Reprovar", "Cancelar")
 * @returns {string|null} null quando não há importada
 */
export function avisoOpcoesNoEvento(n, deQue, acao) {
    const qtd = Number(n) || 0;
    if (qtd <= 0) return null;
    return qtd === 1
        ? `1 opção ${deQue} está nas opções do evento. ${acao} não a retira: o participante continua vendo. Retirar é na Importação das Opções de Aéreo.`
        : `${qtd} opções ${deQue} estão nas opções do evento. ${acao} não as retira: o participante continua vendo. Retirar é na Importação das Opções de Aéreo.`;
}

/**
 * Observação obrigatória de todo pedido e notificação de alteração (e da reabertura). Não é
 * gravada: vai só no corpo do e-mail — e o campo diz isso, para ninguém contar com ela depois na
 * tela. Quem usa trava o Confirmar enquanto ela estiver vazia (`confirmarPreso`). Com
 * `obrigatoria={false}`, é o mesmo campo sem o asterisco.
 */
export function ObservacaoObrigatoria({ valor, onChange, placeholder = 'O que precisa mudar, e por quê', obrigatoria = true }) {
    return (
        <FinField label="Observação" required={obrigatoria} full>
            <FinTextarea rows={4} maxLength={2000} autoFocus
                value={valor} placeholder={placeholder}
                onChange={e => onChange(e.target.value)} />
            <span className="fin-pp__modal-sub" style={{ marginTop: 6 }}>
                Vai no e-mail de quem é notificado. Não fica gravada na solicitação.
            </span>
        </FinField>
    );
}
